import Highlighter from 'react-highlight-words';

import AccessibilityIcon from '@mui/icons-material/Accessibility';
import ExtensionIcon from '@mui/icons-material/Extension';
import { Grid, Icon, MenuItem, Typography } from '@mui/material';
import { makeStyles } from '@mui/styles';

import autocompleteSuggestionStyles from 'UI/components/AutocompleteSuggestion/autocompleteSuggestion.styles.ts';
import { useT } from 'UI/lib';

import CompanySpecificSkillLogo from '../CompanySpecificSkillLogo/CompanySpecificSkillLogo';
import {
  CompanyOpportunity,
  InternalRole,
  JobSynonym,
  SkillSynonym,
  SkillSynonymGlobalSearch,
} from 'GQL/graphql.ts';
import latinize from 'latinize';

const SuggestionIcon = {
  SkillSynonym: <ExtensionIcon />,
  SkillSynonymGlobalSearch: <ExtensionIcon />,
  JobSynonym: <AccessibilityIcon />,
  OrganizationCapability: <AccessibilityIcon />,
  InternalRole: <AccessibilityIcon />,
};

const useStyles = makeStyles(autocompleteSuggestionStyles);

interface RenderSuggestionProps {
  suggestion:
    | JobSynonym
    | CompanyOpportunity
    | SkillSynonym
    | SkillSynonymGlobalSearch
    | InternalRole;
  itemProps: Record<string, any>;
  inputValue: string;
  index: number;
}

function RenderSuggestion({ suggestion, itemProps, inputValue, index }: RenderSuggestionProps) {
  const t = useT();
  const classes = useStyles();

  const { __typename } = suggestion;
  const isJobTitleSearch = __typename === 'CompanyOpportunity';
  const isSkillType = __typename === 'SkillSynonym' || __typename === 'SkillSynonymGlobalSearch';

  const SuggestionLabel = {
    SkillSynonym: t('common:skill'),
    SkillSynonymGlobalSearch: t('common:skill'),
    JobSynonym: t('common:job_title'),
    OrganizationCapability: t('common:organization_capability'),
    InternalRole: t('common:organization_capability'),
  };

  const isLogoVisible = isSkillType ? suggestion?.companySpecific?.isLogoVisible : false;
  const suggestionName =
    __typename === 'JobSynonym' ||
    __typename === 'CompanyOpportunity' ||
    __typename === 'InternalRole'
      ? suggestion.name
      : suggestion.synonymString;

  const associatedSkillName =
    __typename === 'SkillSynonymGlobalSearch' ? suggestion?.associatedSkillName : null;

  return (
    <MenuItem
      {...itemProps}
      key={index}
      component="div"
      data-test="menu-item-autocomplete"
      sx={{
        py: 2,
        px: 8,
        height: 56,
      }}
    >
      <Grid container alignItems="center">
        <Grid item mr={4} display="flex">
          <Icon>{SuggestionIcon[__typename]}</Icon>
        </Grid>
        {isLogoVisible && (
          <Grid item>
            <CompanySpecificSkillLogo
              height={24}
              style={{
                marginRight: 8,
              }}
            />
          </Grid>
        )}

        <Grid item>
          <Typography>
            <Highlighter
              searchWords={[inputValue]}
              autoEscape
              textToHighlight={suggestionName}
              sanitize={latinize}
              highlightClassName={classes.SuggestionHighlight}
            />
          </Typography>
          {associatedSkillName && (
            <Typography variant="caption" component="p">
              <Highlighter
                searchWords={[inputValue]}
                autoEscape
                textToHighlight={t('common:search_result_renamed_from', { associatedSkillName })}
                sanitize={latinize}
                highlightClassName={classes.SuggestionHighlight}
              />
            </Typography>
          )}
        </Grid>
        {!isJobTitleSearch && (
          <Grid
            item
            sx={{
              textTransform: 'capitalize',
              display: 'flex',
              justifyContent: 'end',
              alignSelf: 'center',
            }}
            xs
          >
            <Typography component="span" variant="caption">
              {SuggestionLabel[__typename]}
            </Typography>
          </Grid>
        )}
      </Grid>
    </MenuItem>
  );
}

export default RenderSuggestion;
